import { BaseComponent } from '../base-component'
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaPluginType,
} from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { isEnterKey } from '../../utils/key'
import ClassNamesPlugin from 'embla-carousel-class-names'
import { CarouselComponent } from './carousel'
import { debounce } from '../../utils/debounce'
import { OptionsType } from 'embla-carousel/components/Options'
import { AlignmentOptionType } from 'embla-carousel/components/Alignment'
import { ScrollContainOptionType } from 'embla-carousel/components/ScrollContain'
import { AxisOptionType } from 'embla-carousel/components/Axis'
import { $el, $elParent } from 'src/scripts/utils/utils'

const CAROUSEL_SELECTOR = 'carousel-component'
const CAROUSEL_DOT_SELECTOR = '[data-carousel-dot]'

export class CarouselDots extends BaseComponent {
  embla: EmblaCarouselType
  dots: HTMLElement[]

  mountComponent(): void {
    this.handleCarouselInit()
    this.carousel?.on('changePlayState', this.handleChangePlayState)
  }

  unmountComponent(): void {
    this.carousel?.off('changePlayState', this.handleChangePlayState)
    this.carousel?.embla.off('init', this.handleCarouselInit)
    this.carousel?.embla.off('reInit', this.handleCarouselReInit)
    this.carousel?.embla.off('select', this.handleCarouselSelect)
    this.embla.destroy()
  }

  handleDotClick = (event: MouseEvent): void => {
    const dot = $elParent(CAROUSEL_DOT_SELECTOR, event.target)

    if (!dot) {
      return
    }

    this.scrollToIndex(+dot.dataset.dotIndex)
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const dot = $elParent(CAROUSEL_DOT_SELECTOR, event.target)

    if (!dot) {
      return
    }

    if (isEnterKey(event)) {
      this.scrollToIndex(+dot.dataset.dotIndex)
    }
  }

  handleSettle = (): void => {
    this.update()
  }

  handleCarouselInit = (): void => {
    this.setCarousel()
    this.updateLoadAnimation()
    this.addListener(this, 'click', this.handleDotClick)
    this.addListener(this, 'keydown', this.handleKeyDown)

    this.carousel?.embla.on('reInit', this.handleCarouselReInit)
    this.carousel?.embla.on('select', this.handleCarouselSelect)
    this.embla.on('resize', this.handleResize)
    this.embla.on('settle', this.handleSettle)
    if (this.getAttribute('change-slide-on-scroll')) {
      this.embla.on('select', this.handleSelect)
    }
  }

  handleSelect = (): void => {
    const selectedDotIndex = this.embla.selectedScrollSnap()

    this.carousel.embla.scrollTo(selectedDotIndex)
  }

  handleCarouselReInit = (): void => {
    this.setCarousel()
  }

  handleResize = debounce((): void => {
    this.update()
  }, 250)

  handleCarouselSelect = (): void => {
    if (!this.carousel) {
      return
    }

    const index = this.carousel.embla.selectedScrollSnap()

    this.embla.scrollTo(index)
    this.dots.forEach(dot => {
      dot.classList.toggle('is-primary', +dot.dataset.dotIndex === index)
    })
  }

  handleChangePlayState = (): void => {
    this.updateLoadAnimation()
  }

  updateLoadAnimation() {
    this.classList.toggle('is-autoplaying', this.carousel.isPlay)
  }

  reInit(): void {
    this.embla.reInit(this.getOptions(), this.getPlugins())
  }

  scrollToIndex(index: number): void {
    this.carousel?.embla.scrollTo(index)
    this.carousel?.stop()
  }

  getOptions = (): Partial<OptionsType> => ({
    inViewThreshold: +(this.dataset.inViewThreshhold || 0) || 0.5,
    containScroll: this.getAttribute(
      'data-contain-scroll',
    ) as ScrollContainOptionType,
    align: this.getAttribute('data-align') as AlignmentOptionType,
    skipSnaps: this.hasAttribute('data-skip-snaps'),
    watchDrag: this.hasAttribute('data-draggable'),
    axis: this.getAttribute('data-axis') as AxisOptionType,
  })

  getPlugins = (): EmblaPluginType[] => {
    const plugins: EmblaPluginType[] = [ClassNamesPlugin()]

    if (this.hasAttribute('data-with-wheel-gestures')) {
      plugins.push(
        WheelGesturesPlugin({
          forceWheelAxis: this.getAttribute('data-axis') as AxisOptionType,
        }),
      )
    }

    return plugins
  }

  setCarousel(): void {
    const viewport = $el('[data-carousel-viewport]', this)

    if (!viewport) {
      return
    }

    this.embla = EmblaCarousel(viewport, this.getOptions(), this.getPlugins())
    this.dots = [...this.embla.slideNodes()]
  }

  stop() {
    this.carousel?.stop()
  }

  update(): void {
    const indexList = this.dots.map((_, index) => index)
    const visibleIndexList = this.embla.slidesInView()

    this.dots.forEach((dot, index) => {
      const firstVisibleIndex = visibleIndexList[0]
      const lastVisibleIndex = visibleIndexList[visibleIndexList.length - 1]

      const firstIndex = indexList[0]
      const lastIndex = indexList[indexList.length - 1]

      const isLast = index === lastIndex
      const isLastVisible = index === lastVisibleIndex

      const isFirst = index === firstIndex
      const isFirstVisible = index === firstVisibleIndex

      dot.classList.toggle(
        'is-prev-prev',
        (isFirstVisible || isLastVisible) && !isLast && !isFirst,
      )
    })
  }

  get carousel(): CarouselComponent {
    return $elParent(CAROUSEL_SELECTOR, this)
  }
}
